import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { Stethoscope, FlaskConical, MapPin, Users, Settings, GraduationCap, ClipboardList, FileSearch, Layers, ArrowRight, FileText, ClipboardCheck } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";

// Case images (reuse existing)
import cardioImg from "@/assets/services/clinical-solutions/cases/cardio-heart-failure.jpg";
import vascularImg from "@/assets/services/clinical-solutions/cases/vascular-samd.jpg";
import structuralHeartImg from "@/assets/services/clinical-solutions/cases/structural-heart-pivotal.jpg";
import arrhythmiaImg from "@/assets/services/clinical-solutions/cases/arrhythmia-post-market.jpg";

import agostoniImg from "@/assets/advisors/pierfrancesco-agostoni.png";
import klarenbeekImg from "@/assets/advisors/gijs-klarenbeek.png";

const COLOR = "#FFC60C";
const NAVY = "#002855";
const ACCENT = "#FFC60C";

type Lang = "en" | "es" | "zh-cn";
const pick = <T,>(lang: Lang, m: { en: T; es?: T }): T => (lang === "es" && m.es !== undefined ? m.es : m.en);

const getClientCaseColumns = (lang: Lang) => [
  {
    header: pick(lang, { en: "Vascular | SaMD", es: "Vascular | SaMD" }),
    rows: [
      { icon: Stethoscope, label: pick(lang, { en: "Indication", es: "Indicación" }), value: pick(lang, { en: "Coronary Artery Disease / SaMD", es: "Enfermedad de las arterias coronarias / SaMD" }) },
      { icon: FlaskConical, label: pick(lang, { en: "Study type", es: "Tipo de estudio" }), value: pick(lang, { en: "Post-Market Registry / Dynamic Coronary Roadmap Monitoring / 5 Years", es: "Registro poscomercialización / Monitorización dinámica de mapa coronario / 5 años" }) },
      { icon: MapPin, label: pick(lang, { en: "Sites", es: "Centros" }), value: pick(lang, { en: "4 Sites / Middle-East | Europe | US", es: "4 centros / Oriente Medio | Europa | EE. UU." }) },
      { icon: Users, label: pick(lang, { en: "Subjects", es: "Sujetos" }), value: pick(lang, { en: "394 Subjects", es: "394 sujetos" }) },
      { icon: Settings, label: pick(lang, { en: "Services", es: "Servicios" }), value: pick(lang, { en: "Project Oversight, Monitoring, Site Management, Local Resources", es: "Supervisión de proyecto, monitorización, gestión de centros, recursos locales" }) },
    ],
  },
  {
    header: pick(lang, { en: "Cardio | Heart Failure", es: "Cardio | Insuficiencia cardíaca" }),
    rows: [
      { icon: Stethoscope, label: pick(lang, { en: "Indication", es: "Indicación" }), value: pick(lang, { en: "Ischemic Heart Diseases / In-heart Microcomputer", es: "Cardiopatías isquémicas / Microordenador intracardíaco" }) },
      { icon: FlaskConical, label: pick(lang, { en: "Study type", es: "Tipo de estudio" }), value: pick(lang, { en: "First-In-Human / Study in Close-Out", es: "Primero en humanos / Estudio en cierre" }) },
      { icon: MapPin, label: pick(lang, { en: "Sites", es: "Centros" }), value: pick(lang, { en: "12 Sites / Europe | Middle-East", es: "12 centros / Europa | Oriente Medio" }) },
      { icon: Users, label: pick(lang, { en: "Subjects", es: "Sujetos" }), value: pick(lang, { en: "45 Subjects", es: "45 sujetos" }) },
      { icon: Settings, label: pick(lang, { en: "Services", es: "Servicios" }), value: pick(lang, { en: "Regulatory Submissions, Project Management, Monitoring, Medical Writing, MDR Consulting", es: "Presentaciones regulatorias, gestión de proyectos, monitorización, redacción médica, consultoría MDR" }) },
    ],
  },
  {
    header: pick(lang, { en: "Cardio | Structural Heart", es: "Cardio | Cardiopatía estructural" }),
    rows: [
      { icon: Stethoscope, label: pick(lang, { en: "Indication", es: "Indicación" }), value: pick(lang, { en: "Aortic Valve Disease / Ultrasound Therapy Equipment", es: "Enfermedad valvular aórtica / Equipo de terapia por ultrasonidos" }) },
      { icon: FlaskConical, label: pick(lang, { en: "Study type", es: "Tipo de estudio" }), value: pick(lang, { en: "Pivotal / 1 Year FUP", es: "Pivotal / Seguimiento 1 año" }) },
      { icon: MapPin, label: pick(lang, { en: "Sites", es: "Centros" }), value: pick(lang, { en: "11 Sites / Europe", es: "11 centros / Europa" }) },
      { icon: Users, label: pick(lang, { en: "Subjects", es: "Sujetos" }), value: pick(lang, { en: "60 Subjects", es: "60 sujetos" }) },
      { icon: Settings, label: pick(lang, { en: "Services", es: "Servicios" }), value: pick(lang, { en: "Full CRO Services, Regulatory Submissions, Biostatistics, Safety & Data Management, Core Lab, CEC & DSMB", es: "Servicios CRO integrales, presentaciones regulatorias, bioestadística, gestión de seguridad y datos, core lab, CEC y DSMB" }) },
    ],
  },
  {
    header: pick(lang, { en: "Cardio | Arrhythmia", es: "Cardio | Arritmia" }),
    rows: [
      { icon: Stethoscope, label: pick(lang, { en: "Indication", es: "Indicación" }), value: pick(lang, { en: "Atrial Fibrillation / Left Atrial Appendage Exclusion", es: "Fibrilación auricular / Exclusión de la orejuela auricular izquierda" }) },
      { icon: FlaskConical, label: pick(lang, { en: "Study type", es: "Tipo de estudio" }), value: pick(lang, { en: "Post-Market / Study in Enrolment", es: "Poscomercialización / Estudio en reclutamiento" }) },
      { icon: MapPin, label: pick(lang, { en: "Sites", es: "Centros" }), value: pick(lang, { en: "Up to 24 Sites / Europe | Asia-Pac", es: "Hasta 24 centros / Europa | Asia-Pacífico" }) },
      { icon: Users, label: pick(lang, { en: "Subjects", es: "Sujetos" }), value: pick(lang, { en: "Up to 6500 Subjects Forecasted (globally)", es: "Hasta 6500 sujetos previstos (a nivel global)" }) },
      { icon: Settings, label: pick(lang, { en: "Services", es: "Servicios" }), value: pick(lang, { en: "Regulatory Submissions, Site Management, Monitoring, OUS Oversight, Rescue Project", es: "Presentaciones regulatorias, gestión de centros, monitorización, supervisión OUS, proyecto de rescate" }) },
    ],
  },
];

const getAdvisors = (lang: Lang) => [
  {
    name: "Pierfrancesco Agostoni",
    title: pick(lang, { en: "Interventional Cardiologist", es: "Cardiólogo intervencionista" }),
    image: agostoniImg,
    bullets: pick(lang, {
      en: [
        "20+ years of experience in invasive and interventional cardiology",
        "Performs 300–400 complex PCIs and approximately 80 TAVI procedures per year",
        "Expert in CTO interventions, radial access, and advanced percutaneous techniques",
        "310+ PubMed-indexed publications and 10+ book chapters",
      ],
      es: [
        "Más de 20 años de experiencia en cardiología invasiva e intervencionista",
        "Realiza entre 300 y 400 ICP complejas y aproximadamente 80 procedimientos TAVI al año",
        "Experto en intervenciones de CTO, acceso radial y técnicas percutáneas avanzadas",
        "Más de 310 publicaciones indexadas en PubMed y más de 10 capítulos de libro",
      ],
    }),
  },
  {
    name: "Gijs Klarenbeek",
    title: pick(lang, { en: "Physician & Clinical Development Expert", es: "Médico y experto en desarrollo clínico" }),
    image: klarenbeekImg,
    bullets: pick(lang, {
      en: [
        "Medical doctor with training in general surgery",
        "Extensive experience in clinical development and regulatory approval of medical devices",
        "Therapeutic expertise in cardiovascular, respiratory, oncology, diabetes, and more",
      ],
      es: [
        "Médico con formación en cirugía general",
        "Amplia experiencia en desarrollo clínico y aprobación regulatoria de dispositivos médicos",
        "Experiencia terapéutica en cardiovascular, respiratorio, oncología, diabetes y más",
      ],
    }),
  },
];

const getExpertiseAreas = (lang: Lang): { icon: LucideIcon; title: string; sub?: string[] }[] => [
  { icon: ClipboardList, title: pick(lang, { en: "Strategic & scientific advice", es: "Asesoramiento estratégico y científico" }) },
  {
    icon: FileSearch,
    title: pick(lang, { en: "Clinical study support", es: "Soporte al estudio clínico" }),
    sub: pick(lang, {
      en: ["Input study design/protocol", "Act as independent Medical Monitor", "Act as CEC/DSMB member"],
      es: ["Contribución al diseño y protocolo del estudio", "Actuación como Medical Monitor independiente", "Participación como miembro del CEC/DSMB"],
    }),
  },
  {
    icon: Layers,
    title: pick(lang, { en: "Clinical evidence support", es: "Soporte a la evidencia clínica" }),
    sub: pick(lang, {
      en: ["Clinical Development planning", "Support Risk assessment", "Validate clinical relevance"],
      es: ["Planificación del desarrollo clínico", "Apoyo a la evaluación de riesgos", "Validación de la relevancia clínica"],
    }),
  },
  { icon: GraduationCap, title: pick(lang, { en: "Therapeutic training for QbD Group employees", es: "Formación terapéutica para los profesionales de QbD Group" }) },
];

const getClientCases = (lang: Lang) => [
  {
    title: pick(lang, { en: "Heart Failure Study, First-In-Human Device", es: "Estudio de insuficiencia cardíaca, dispositivo first-in-human" }),
    tag: pick(lang, { en: "Heart Failure", es: "Insuficiencia cardíaca" }),
    bullets: pick(lang, {
      en: [
        "Supported a first-in-human clinical study for an in-heart microcomputer treating ischemic heart disease.",
        "Managed 12 sites across Europe and the Middle East, involving 45 enrolled subjects.",
        "Delivered end-to-end clinical support: regulatory submissions, project and site management, monitoring, and medical writing.",
        "Provided MDR compliance consulting throughout the trial lifecycle.",
      ],
      es: [
        "Apoyamos un estudio clínico first-in-human para un microordenador intracardíaco destinado al tratamiento de la cardiopatía isquémica.",
        "Gestionamos 12 centros en Europa y Oriente Medio, con 45 sujetos reclutados.",
        "Ofrecimos soporte clínico integral: presentaciones regulatorias, gestión de proyecto y centros, monitorización y redacción médica.",
        "Proporcionamos consultoría de cumplimiento MDR durante todo el ciclo del ensayo.",
      ],
    }),
    image: cardioImg,
  },
  {
    title: pick(lang, { en: "Structural Heart Study, Pivotal Trial in EU", es: "Estudio de cardiopatía estructural, ensayo pivotal en la UE" }),
    tag: pick(lang, { en: "Structural Heart", es: "Cardiopatía estructural" }),
    bullets: pick(lang, {
      en: [
        "Oversaw a pivotal study for an ultrasound therapy device targeting aortic valve disease.",
        "Managed 11 sites across Europe, enrolling 60 subjects with one-year follow-up.",
        "Delivered full-service CRO support, including regulatory submissions, biostatistics, safety and data management, and core lab coordination.",
        "Facilitated CEC and DSMB oversight to ensure robust clinical governance.",
      ],
      es: [
        "Supervisamos un estudio pivotal para un dispositivo de terapia por ultrasonidos dirigido a la enfermedad valvular aórtica.",
        "Gestionamos 11 centros en Europa, con 60 sujetos reclutados y un año de seguimiento.",
        "Ofrecimos soporte CRO integral, incluyendo presentaciones regulatorias, bioestadística, gestión de seguridad y datos, y coordinación del core lab.",
        "Facilitamos la supervisión del CEC y DSMB para garantizar una sólida gobernanza clínica.",
      ],
    }),
    image: structuralHeartImg,
  },
  {
    title: pick(lang, { en: "Post-Market Study, Arrhythmia Device", es: "Estudio poscomercialización, dispositivo para arritmia" }),
    tag: pick(lang, { en: "Arrhythmia", es: "Arritmia" }),
    bullets: pick(lang, {
      en: [
        "Supporting a large-scale post-market study for a device treating atrial fibrillation and left atrial appendage exclusion.",
        "Coordinating up to 24 sites across Europe and Asia-Pacific, with a global forecast of 6,500+ subjects.",
        "Stepped in as a rescue CRO, taking over monitoring, site management, and regulatory submissions mid-study.",
        "Ensuring smooth OUS oversight and operational continuity across regions.",
      ],
      es: [
        "Damos soporte a un estudio poscomercialización a gran escala para un dispositivo destinado al tratamiento de la fibrilación auricular y a la exclusión de la orejuela auricular izquierda.",
        "Coordinamos hasta 24 centros en Europa y Asia-Pacífico, con una previsión global de más de 6.500 sujetos.",
        "Intervinimos como CRO de rescate, asumiendo la monitorización, la gestión de centros y las presentaciones regulatorias a mitad del estudio.",
        "Garantizamos una supervisión OUS fluida y continuidad operativa entre regiones.",
      ],
    }),
    image: arrhythmiaImg,
  },
  {
    title: pick(lang, { en: "Vascular SaMD, Post-Market Registry", es: "SaMD vascular, registro poscomercialización" }),
    tag: pick(lang, { en: "Vascular", es: "Vascular" }),
    bullets: pick(lang, {
      en: [
        "Managed a five-year post-market registry for a coronary artery disease SaMD.",
        "Operated across 4 sites in the Middle East, Europe, and the US, enrolling 394 subjects.",
        "Provided local resource coordination, site management, and ongoing project oversight.",
        "Supported dynamic coronary roadmap monitoring and long-term safety tracking.",
      ],
      es: [
        "Gestionamos un registro poscomercialización de cinco años para un SaMD de enfermedad de las arterias coronarias.",
        "Operamos en 4 centros en Oriente Medio, Europa y EE. UU., con 394 sujetos reclutados.",
        "Proporcionamos coordinación de recursos locales, gestión de centros y supervisión continua del proyecto.",
        "Apoyamos la monitorización dinámica del mapa coronario y el seguimiento de seguridad a largo plazo.",
      ],
    }),
    image: vascularImg,
  },
];

const getResources = (lang: Lang) => [
  {
    type: pick(lang, { en: "Self-Assessment", es: "Autoevaluación" }),
    title: pick(lang, { en: "EU Readiness Assessment for Cardiology Trials", es: "Evaluación de preparación para ensayos de cardiología en la UE" }),
    description: pick(lang, { en: "Check your trial readiness across MDR, ISO 14155, and European site requirements.", es: "Comprueba tu preparación para ensayos clínicos según el MDR, la ISO 14155 y los requisitos de centros europeos." }),
    href: lang === "es" ? "/es/eu-readiness-cardiology" : "/en/eu-readiness-cardiology",
    icon: ClipboardCheck,
    image: structuralHeartImg,
  },
  {
    type: pick(lang, { en: "Blog", es: "Blog" }),
    title: pick(lang, { en: "First Structural Heart Trial in Europe", es: "Primer ensayo de cardiopatía estructural en Europa" }),
    description: pick(lang, { en: "Key considerations when running your first structural heart device study in the European market.", es: "Aspectos clave al realizar tu primer estudio de dispositivos para cardiopatía estructural en el mercado europeo." }),
    href: "/en/blog/first-structural-heart-trial-europe",
    icon: FileText,
    image: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/blog-images/first-structural-heart-trial-europe-hero.jpg",
  },
  {
    type: pick(lang, { en: "Blog", es: "Blog" }),
    title: pick(lang, { en: "Structural Heart & Coronary Trial Pitfalls in Europe", es: "Errores comunes en ensayos de cardiopatía estructural y coronaria en Europa" }),
    description: pick(lang, { en: "Avoid the most common operational and regulatory mistakes in cardiovascular trials.", es: "Evita los errores operativos y regulatorios más frecuentes en ensayos cardiovasculares." }),
    href: "/en/blog/structural-heart-coronary-trial-pitfalls-europe",
    icon: FileText,
    image: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/blog-images/structural-heart-coronary-trial-pitfalls-europe-hero.jpg",
  },
  {
    type: pick(lang, { en: "Blog", es: "Blog" }),
    title: pick(lang, { en: "Structural Heart & Coronary Trial Feasibility in Europe", es: "Viabilidad de ensayos de cardiopatía estructural y coronaria en Europa" }),
    description: pick(lang, { en: "How to assess site feasibility, patient pathways, and evidence requirements before you start.", es: "Cómo evaluar la viabilidad de centros, las vías de pacientes y los requisitos de evidencia antes de comenzar." }),
    href: "/en/blog/structural-heart-coronary-trial-feasibility-europe",
    icon: FileText,
    image: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/blog-images/blog%2Fstructural-heart-coronary-trial-feasibility-europe-featured.jpg",
  },
  {
    type: pick(lang, { en: "Blog", es: "Blog" }),
    title: pick(lang, { en: "Cardiac Device Trials in Europe: MDR Strategy", es: "Ensayos de dispositivos cardíacos en Europa: Estrategia MDR" }),
    description: pick(lang, { en: "A practical guide to aligning your cardiac device trial strategy with EU MDR expectations.", es: "Una guía práctica para alinear la estrategia de ensayos de dispositivos cardíacos con las expectativas del MDR de la UE." }),
    href: "/en/blog/cardiac-device-trials-europe-mdr-strategy",
    icon: FileText,
    image: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/blog-images/blog%2Fcardiac-device-trials-europe-mdr-strategy-featured.jpg",
  },
];

const getAnchorSections = (lang: Lang) => [
  { id: "cardio-cases-overview", label: pick(lang, { en: "Cases Overview", es: "Resumen de casos" }) },
  { id: "cardio-intro", label: pick(lang, { en: "Overview", es: "Descripción general" }) },
  { id: "cardio-advisory", label: pick(lang, { en: "Medical Advisory Board", es: "Consejo Asesor Médico" }) },
  { id: "cardio-cases", label: pick(lang, { en: "Case Studies", es: "Casos de éxito" }) },
  { id: "cardio-resources", label: pick(lang, { en: "Resources", es: "Recursos" }) },
];

const anim = (i: number) => ({
  initial: { opacity: 0, y: 16 } as const,
  whileInView: { opacity: 1, y: 0 } as const,
  viewport: { once: true } as const,
  transition: { duration: 0.4, delay: i * 0.08 },
});

import AnchorNav from "./AnchorNav";

interface Props {
  onCtaClick: () => void;
  offeringsSlot?: React.ReactNode;
}

const CardiologyCROContent = ({ onCtaClick, offeringsSlot }: Props) => {
  const lang = useLanguage() as Lang;
  const t = uiT(lang);

  const clientCaseColumns = getClientCaseColumns(lang);
  const advisors = getAdvisors(lang);
  const expertiseAreas = getExpertiseAreas(lang);
  const clientCases = getClientCases(lang);
  const resources = getResources(lang);
  const anchorSections = getAnchorSections(lang);

  const L = {
    clientCases: pick(lang, { en: "Stents and cardiac diagnostics", es: "Stents y diagnóstico cardíaco" }),
    cardiologyClientCases: pick(lang, { en: "Cardiology Client Cases", es: "Casos de clientes en cardiología" }),
    medicalAdvisoryBoard: pick(lang, { en: "Scientific Counsel", es: "Asesoramiento científico" }),
    meetTheBoard: pick(lang, { en: "Meet the Medical Advisory Board", es: "Conoce al Consejo Asesor Médico" }),
    boardIntro: pick(lang, {
      en: "Our Medical Advisory Board brings together experienced life sciences professionals who provide expert guidance on clinical strategy, patient safety, scientific rigor, and regulatory compliance.",
      es: "Nuestro Consejo Asesor Médico reúne a profesionales con amplia trayectoria en ciencias de la vida que aportan orientación experta en estrategia clínica, seguridad del paciente, rigor científico y cumplimiento normativo.",
    }),
    areasOfExpertise: pick(lang, { en: "Areas of expertise", es: "Áreas de especialización" }),
    independentBoard: pick(lang, { en: "QbD Clinical's Independent Medical Advisory Board", es: "Consejo Asesor Médico Independiente de QbD Clinical" }),
    caseStudies: pick(lang, { en: "Cardiovascular Trial Delivery", es: "Trayectoria en ensayos cardíacos" }),
    featuredCRO: pick(lang, { en: "Featured CRO Case Studies", es: "Casos de éxito destacados como CRO" }),
    featuredCROIntro: pick(lang, {
      en: "We bring hands-on expertise to complex cardiovascular studies, from first-in-human trials to global post-market programs.",
      es: "Aportamos experiencia práctica en estudios cardiovasculares complejos, desde ensayos first-in-human hasta programas globales de poscomercialización.",
    }),
    resources: pick(lang, { en: "European Trial Planning", es: "Planificación de ensayos UE" }),
    relatedResources: pick(lang, { en: "Related Resources", es: "Recursos relacionados" }),
    resourcesIntro: pick(lang, {
      en: "Explore practical guidance, assessments, and insights for cardiology device trials in Europe.",
      es: "Explora guía práctica, evaluaciones y conocimientos para ensayos de dispositivos de cardiología en Europa.",
    }),
    introP1: pick(lang, {
      en: <>At QbD Clinical, we know your priority is delivering innovative <strong className="text-foreground font-medium">cardiology medical devices</strong> to patients, safely and efficiently. That's why we don't just run clinical trials; we collaborate with you to drive results.</>,
      es: <>En QbD Clinical sabemos que tu prioridad es hacer llegar a los pacientes <strong className="text-foreground font-medium">dispositivos médicos de cardiología</strong> innovadores, de forma segura y eficiente. Por eso no nos limitamos a ejecutar ensayos clínicos: colaboramos contigo para impulsar resultados.</>,
    }),
    introP2: pick(lang, {
      en: <>As a European CRO dedicated exclusively to medical devices, we offer deep expertise in <strong className="text-foreground font-medium">Cardiology Medical Device clinical research</strong>. From heart failure devices and stents to structural heart innovations and cardiovascular diagnostics, we support your journey from strategy to post-market follow-up.</>,
      es: <>Como CRO europea dedicada exclusivamente a dispositivos médicos, contamos con una profunda experiencia en <strong className="text-foreground font-medium">investigación clínica de dispositivos médicos de cardiología</strong>. Desde dispositivos para insuficiencia cardíaca y stents hasta innovaciones en cardiopatía estructural y diagnóstico cardiovascular, te acompañamos desde la estrategia hasta el seguimiento poscomercialización.</>,
    }),
    introP3: pick(lang, {
      en: <>We help you navigate complexity, accelerate timelines, and ensure <strong className="text-foreground font-medium">MDR and ISO 14155 compliance</strong>, so you can move your cardiology innovations forward with confidence.</>,
      es: <>Te ayudamos a gestionar la complejidad, acortar plazos y garantizar el <strong className="text-foreground font-medium">cumplimiento del MDR y de la ISO 14155</strong>, para que puedas impulsar tus innovaciones en cardiología con plena confianza.</>,
    }),
  };

  return (
    <>
      {/* Anchor nav */}
      <AnchorNav sections={anchorSections} />

      {/* Intro – full text, no toggle */}
      <section id="cardio-intro" className="py-10 sm:py-14 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl space-y-3">
          <p className="text-sm text-muted-foreground leading-relaxed">{L.introP1}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{L.introP2}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{L.introP3}</p>
        </div>
      </section>

      {/* Cardiology Client Cases – 4-column overview */}
      <section id="cardio-cases-overview" className="py-12 sm:py-16 scroll-mt-28" style={{ backgroundColor: "#F6F8FB" }}>
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: ACCENT }} />
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: NAVY }}>{L.clientCases}</p>
          </div>
          <h2
            className="text-2xl sm:text-3xl font-bold mb-8"
            style={{ color: NAVY }}
          >
            {L.cardiologyClientCases}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {clientCaseColumns.map((col, i) => (
              <motion.div
                key={col.header}
                className="rounded-xl bg-white border border-border/40 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                {...anim(i)}
              >
                <div className="px-5 py-3" style={{ backgroundColor: NAVY }}>
                  <h3 className="text-sm font-semibold text-white text-center">{col.header}</h3>
                </div>
                <ul className="p-4 space-y-3">
                  {col.rows.map((row, j) => {
                    const Icon = row.icon;
                    return (
                      <li key={j} className="flex gap-3">
                        <span
                          className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center"
                          style={{ backgroundColor: `${ACCENT}1F` }}
                        >
                          <Icon size={14} strokeWidth={1.75} style={{ color: NAVY }} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: ACCENT }}>
                            {row.label}
                          </p>
                          <p className="text-xs text-foreground leading-snug">{row.value}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advisory Board */}
      <section id="cardio-advisory" className="py-12 sm:py-16 scroll-mt-28 bg-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: ACCENT }} />
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: NAVY }}>{L.medicalAdvisoryBoard}</p>
          </div>
          <h2
            className="text-2xl sm:text-3xl font-bold mb-3"
            style={{ color: NAVY }}
          >
            {L.meetTheBoard}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8 max-w-3xl">
            {L.boardIntro}
          </p>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Advisor cards */}
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              {advisors.map((a, i) => (
                <motion.div
                  key={a.name}
                  className="rounded-xl bg-white border border-border/40 p-6 shadow-sm"
                  {...anim(i)}
                >
                  <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border/40">
                    <img
                      src={a.image}
                      alt={a.name}
                      loading="lazy"
                      className="w-20 h-20 rounded-full object-cover shrink-0 ring-2"
                      style={{ ['--tw-ring-color' as never]: ACCENT, boxShadow: `0 0 0 2px ${ACCENT}` }}
                    />
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold leading-tight" style={{ color: NAVY }}>{a.name}</h3>
                      <p className="text-xs font-medium mt-1" style={{ color: ACCENT }}>{a.title}</p>
                    </div>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-2">
                    {a.bullets.map((b, j) => (
                      <li key={j} className="flex gap-2">
                        <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: ACCENT }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Expertise highlight */}
            <motion.div
              className="rounded-xl p-5 shadow-sm"
              style={{ background: `linear-gradient(160deg, ${NAVY} 0%, #013a7a 100%)` }}
              {...anim(2)}
            >
              <p className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: ACCENT }}>
                {L.areasOfExpertise}
              </p>
              <h3 className="text-sm font-semibold text-white mb-4 leading-snug">
                {L.independentBoard}
              </h3>
              <ol className="space-y-3">
                {expertiseAreas.map((area, i) => {
                  const Icon = area.icon;
                  return (
                    <li key={i} className="flex gap-3">
                      <span
                        className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: `${ACCENT}33` }}
                      >
                        <Icon size={14} strokeWidth={1.75} style={{ color: ACCENT }} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-white leading-snug">
                          {area.title}
                        </p>
                        {area.sub && (
                          <ul className="mt-1 space-y-0.5 text-[11px] text-white/70">
                            {area.sub.map((s, k) => (
                              <li key={k} className="flex gap-1.5">
                                <span style={{ color: ACCENT }}>•</span>
                                <span>{s}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Offerings slot */}
      <div style={{ backgroundColor: "#F6F8FB" }}>{offeringsSlot}</div>

      {/* Client Cases – existing detailed */}
      <section id="cardio-cases" className="py-12 sm:py-16 bg-muted/30 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{L.caseStudies}</p>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3">{L.featuredCRO}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8 max-w-3xl">
            {L.featuredCROIntro}
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {clientCases.map((c, i) => (
              <motion.div
                key={c.title}
                className="group rounded-lg overflow-hidden border border-border/40 bg-background hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                {...anim(i)}
              >
                {c.image && (
                  <div className="relative">
                    <img src={c.image} alt={c.title} className="w-full h-52 object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                    <span
                      className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                      style={{ backgroundColor: COLOR, color: "#1a1a1a" }}
                    >
                      {c.tag}
                    </span>
                  </div>
                )}
                {!c.image && (
                  <div className="px-5 pt-4">
                    <span
                      className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                      style={{ backgroundColor: COLOR, color: "#1a1a1a" }}
                    >
                      {c.tag}
                    </span>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2">{c.title}</h3>
                  <ul className="text-xs text-muted-foreground space-y-1.5">
                    {c.bullets.map((b, j) => (
                      <li key={j} className="flex gap-2">
                        <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: COLOR }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <button onClick={onCtaClick} className="mt-3 text-xs font-medium text-primary hover:underline">
                    {t("getInTouchArrow")}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section id="cardio-resources" className="py-12 sm:py-16 bg-white scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{L.resources}</p>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3">{L.relatedResources}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8 max-w-3xl">
            {L.resourcesIntro}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.title}
                  className="group rounded-xl border border-border/40 bg-background overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                  {...anim(i)}
                >
                  <Link to={r.href} className="block aspect-[16/9] overflow-hidden bg-muted">
                    <img
                      src={r.image}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                    />
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <span
                        className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: `${COLOR}1F` }}
                      >
                        <Icon size={16} strokeWidth={1.75} style={{ color: NAVY }} />
                      </span>
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full self-start"
                        style={{ backgroundColor: `${COLOR}26`, color: "#1a1a1a" }}
                      >
                        {r.type}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-2 leading-snug">
                      {r.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      {r.description}
                    </p>
                    <Link
                      to={r.href}
                      className="mt-auto inline-flex items-center gap-1 text-xs font-semibold hover:gap-2 transition-all"
                      style={{ color: NAVY }}
                    >
                      {t("readMore")} <ArrowRight size={12} />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default CardiologyCROContent;
